/**
 * The one module that talks to the three Phase 3 tables — `day_exceptions`
 * (`0007`), `recurrences` (`0008`) and `shifts` (`0009`) — and maps their
 * snake_case Postgres rows onto the engine's camelCase types. Spec:
 * `plans/phase-3-availability-fwa.md` §1 Client (the `src/lib/roster-data.ts`
 * row and the "Regeneration safety", "Lazy extension" and "Collision ruling"
 * paragraphs that follow it).
 *
 * Conventions, inherited from `src/lib/profile.ts`:
 *
 * - every call returns a `{ data, error }`-shaped result with
 *   `PostgrestError | null` — no throws across the Supabase boundary;
 * - every query filters the owner explicitly with `.eq()`, never trusting RLS
 *   alone to narrow the statement;
 * - dates are `'YYYY-MM-DD'` strings end to end. Postgres `date` columns
 *   arrive from PostgREST already in exactly that shape (`0007`'s header
 *   comment records why), so they pass through **verbatim** — never through
 *   `new Date()`, which is a timezone waiting to happen.
 *
 * Client-renderable failures come back as typed values, not bare errors, so
 * the sheets can render a sentence instead of a code:
 *
 * - `addFwaShift` surfaces the unique-violation (SQLSTATE 23505) case as
 *   `duplicate: true` — "you already have that shift on that day";
 * - `saveRecurrence`/`deleteRecurrence` surface the regeneration-safety guard
 *   as a `RosterDataError` — `{ kind: 'NON_ROSTERED_ROWS', count }`.
 *
 * Horizon: generation (not entry) is bounded by `ACTAS_ROSTER.horizon`'s
 * forward reach — six months from today. The bound is enforced in the UI
 * (Tasks 5/8); here it caps how far a rule generates rows, so an open-ended
 * rule cannot be tricked into generating a century. `ensureRecurrenceCoverage`
 * takes `today` as a parameter and `saveRecurrence` as an optional one, so
 * tests stay offline and deterministic.
 */

import { PostgrestError } from '@supabase/supabase-js'
import { supabase } from './supabase'
import { todayIso } from './today'
import { ACTAS_ROSTER } from '../data/roster-config'
import { addDays, addMonths } from '../engine/civil'
import {
  expandRecurrence,
  expandRecurrenceToRows,
  validateRecurrenceRule,
} from '../engine/recurrence'
import type { RecurrenceRule } from '../engine/recurrence'
import type {
  DateRange,
  DayException,
  ExplicitShift,
  IsoDate,
  ShiftCode,
} from '../engine/types'

// ---------------------------------------------------------------------------
// Public result shapes
// ---------------------------------------------------------------------------

/** The three tables, merged into the engine's own vocabulary. */
export interface RosterData {
  exceptions: DayException[]
  explicit: ExplicitShift[]
  recurrences: RecurrenceRule[]
}

/**
 * A failure the client is expected to render as a sentence, distinct from a
 * `PostgrestError` (infrastructure / unexpected) and from `null` (success).
 * The `kind` discriminator is what a sheet switches on; `count` feeds the
 * sentence ("This rule has 2 shifts that aren't rostered — withdraw them
 * first"). Written as a union so later kinds slot in without changing any
 * caller's switch shape.
 */
export type RosterDataError = {
  kind: 'NON_ROSTERED_ROWS'
  /** How many of the rule's rows carry a status other than ROSTERED. */
  count: number
}

/** `saveRecurrence`'s three outcomes: saved, guard-blocked, or errored. */
export type SaveRecurrenceResult =
  | { rule: RecurrenceRule; skippedCount: number; guard: null; error: null }
  | { rule: null; skippedCount: null; guard: RosterDataError; error: null }
  | { rule: null; skippedCount: null; guard: null; error: PostgrestError }

/** `deleteRecurrence`'s three outcomes: deleted, guard-blocked, or errored. */
export type DeleteRecurrenceResult =
  | { guard: null; error: null }
  | { guard: RosterDataError; error: null }
  | { guard: null; error: PostgrestError }

/** `addFwaShift`'s three outcomes: added, duplicate, or errored. */
export type AddFwaShiftResult =
  | { shift: ExplicitShift; duplicate: false; error: null }
  | { shift: null; duplicate: true; error: null }
  | { shift: null; duplicate: false; error: PostgrestError }

// ---------------------------------------------------------------------------
// Postgres rows (snake_case, exactly as PostgREST returns them) → engine types
// ---------------------------------------------------------------------------

/** One `day_exceptions` row (`0007`). `date` is already `'YYYY-MM-DD'`. */
interface DayExceptionRow {
  id: string
  user_id: string
  date: IsoDate
  kind: DayException['kind']
  note: string | null
}

/** One `shifts` row (`0009`). `work_date` is already `'YYYY-MM-DD'`. */
interface ShiftRow {
  id: string
  owner_user_id: string
  work_date: IsoDate
  shift_code: ShiftCode
  origin: ExplicitShift['origin']
  status: ExplicitShift['status']
  recurrence_id: string | null
}

/** One `recurrences` row (`0008`). Dates are already `'YYYY-MM-DD'`. */
interface RecurrenceRow {
  id: string
  owner_user_id: string
  kind: RecurrenceRule['kind']
  interval_days: number | null
  weekday: number | null
  weeks_interval: number | null
  shift_code: ShiftCode
  start_date: IsoDate
  end_date: IsoDate | null
  exception_dates: IsoDate[] | null
}

function toDayException(row: DayExceptionRow): DayException {
  const exception: DayException = { id: row.id, date: row.date, kind: row.kind }
  // Absent, not null: `note` is optional on the engine type, and a null-valued
  // key would make every `toEqual` in a fixture disagree with a hand-built one.
  if (row.note != null) exception.note = row.note
  return exception
}

function toExplicitShift(row: ShiftRow): ExplicitShift {
  return {
    id: row.id,
    date: row.work_date,
    code: row.shift_code,
    origin: row.origin,
    status: row.status,
  }
}

function toRecurrenceRule(row: RecurrenceRow): RecurrenceRule {
  return {
    id: row.id,
    kind: row.kind,
    intervalDays: row.interval_days,
    weekday: row.weekday,
    weeksInterval: row.weeks_interval,
    shiftCode: row.shift_code,
    startDate: row.start_date,
    endDate: row.end_date,
    exceptionDates: row.exception_dates ?? [],
  }
}

/** The inverse of `toRecurrenceRule`, minus `id` (added by the caller). */
function ruleToRow(rule: RecurrenceRule, userId: string) {
  return {
    owner_user_id: userId,
    kind: rule.kind,
    interval_days: rule.intervalDays ?? null,
    weekday: rule.weekday ?? null,
    weeks_interval: rule.weeksInterval ?? null,
    shift_code: rule.shiftCode,
    start_date: rule.startDate,
    end_date: rule.endDate ?? null,
    exception_dates: rule.exceptionDates,
  }
}

// ---------------------------------------------------------------------------
// fetchRosterData
// ---------------------------------------------------------------------------

/**
 * Everything the calendar needs for `userId` inside `range` (both ends
 * inclusive), in one round trip per table, run in parallel:
 *
 * - `day_exceptions` filtered to `date` inside the range;
 * - `shifts` filtered to `work_date` inside the range;
 * - the user's `recurrences` **unfiltered by range** — a rule's generated rows
 *   may need extension well past where the rule's `start_date` sits (plan §1
 *   Client, "Lazy extension"), so the rule list cannot be clipped to the
 *   visible month.
 *
 * Any of the three queries erroring fails the whole fetch: a calendar built
 * from two-thirds of its inputs would look plausible while being wrong.
 */
export async function fetchRosterData(
  userId: string,
  range: DateRange,
): Promise<{ data: RosterData | null; error: PostgrestError | null }> {
  const [exceptionRes, shiftRes, recurrenceRes] = await Promise.all([
    supabase
      .from('day_exceptions')
      .select('*')
      .eq('user_id', userId)
      .gte('date', range.from)
      .lte('date', range.to),
    supabase
      .from('shifts')
      .select('*')
      .eq('owner_user_id', userId)
      .gte('work_date', range.from)
      .lte('work_date', range.to),
    supabase.from('recurrences').select('*').eq('owner_user_id', userId),
  ])

  const error = exceptionRes.error ?? shiftRes.error ?? recurrenceRes.error
  if (error) return { data: null, error }

  return {
    data: {
      exceptions: (exceptionRes.data as DayExceptionRow[]).map(toDayException),
      explicit: (shiftRes.data as ShiftRow[]).map(toExplicitShift),
      recurrences: (recurrenceRes.data as RecurrenceRow[]).map(toRecurrenceRule),
    },
    error: null,
  }
}

// ---------------------------------------------------------------------------
// Lazy extension
// ---------------------------------------------------------------------------

/** The forward horizon for `today`: six months out, per `ACTAS_ROSTER`. */
function horizonForward(today: IsoDate): IsoDate {
  return addMonths(today, ACTAS_ROSTER.horizon.monthsForward)
}

/**
 * Extends each of the user's recurrence rules whose generated `shifts` rows
 * stop short of the window the calendar is about to show. Runs after every
 * calendar load (plan §1 Client, "Lazy extension").
 *
 * For each rule: the generation target is `min(range.to, endDate ?? horizon)`
 * — an open-ended rule is capped at the forward horizon, a closed rule at its
 * own `end_date`, and neither is ever extended past what the visible month
 * actually needs. If the rule's rows already reach that target, nothing
 * happens. Otherwise the missing dates are computed by `expandRecurrence`
 * starting from the day after the last generated row — the engine keeps the
 * rule's phase across a mid-pattern window, so "first uncovered day" is a
 * correct window start — and inserted with
 * `on conflict (owner_user_id, work_date, shift_code) do nothing`.
 *
 * That conflict clause is what makes this idempotent without a lock: two tabs
 * racing the same extension both no-op, and a generated date that collides
 * with a hand-added `FWA_MANUAL` shift is skipped — manual wins (the plan's
 * collision ruling; the UNIQUE constraint is the authority).
 *
 * @param userId - The rules' owner; every statement also filters on it.
 * @param range - The window the calendar is about to render.
 * @param today - Injected, not read from the clock — the horizon anchor.
 */
export async function ensureRecurrenceCoverage(
  userId: string,
  range: DateRange,
  today: IsoDate,
): Promise<{ error: PostgrestError | null }> {
  const { data: ruleRows, error } = await supabase
    .from('recurrences')
    .select('*')
    .eq('owner_user_id', userId)
  if (error) return { error }

  const horizon = horizonForward(today)

  for (const raw of ruleRows as RecurrenceRow[]) {
    const rule = toRecurrenceRule(raw)
    if (rule.id == null) continue // cannot happen for a fetched row; keeps rule.id a string below

    // min(range.to, coalesce(end_date, horizon)) — string comparison is
    // chronological for 'YYYY-MM-DD'.
    const bound = rule.endDate ?? horizon
    const target = range.to <= bound ? range.to : bound

    const { data: existing, error: rowsError } = await supabase
      .from('shifts')
      .select('work_date')
      .eq('recurrence_id', rule.id)
      .eq('owner_user_id', userId)
    if (rowsError) return { error: rowsError }

    const maxWorkDate = (existing as Array<{ work_date: IsoDate }>).reduce<IsoDate | null>(
      (max, row) => (max === null || row.work_date > max ? row.work_date : max),
      null,
    )
    const from = maxWorkDate !== null ? addDays(maxWorkDate, 1) : rule.startDate
    if (from > target) continue // already covered past the visible window

    const missing = expandRecurrence(rule, { from, to: target })
    if (missing.length === 0) continue

    const { error: insertError } = await supabase
      .from('shifts')
      .upsert(
        missing.map((work_date) => ({
          owner_user_id: userId,
          work_date,
          shift_code: rule.shiftCode,
          origin: 'FWA_RECURRING' as const,
          recurrence_id: rule.id as string,
        })),
        { onConflict: 'owner_user_id,work_date,shift_code', ignoreDuplicates: true },
      )
    if (insertError) return { error: insertError }
  }

  return { error: null }
}

// ---------------------------------------------------------------------------
// day_exceptions
// ---------------------------------------------------------------------------

/**
 * Marks (or re-marks) one of the user's days as LEAVE / UNAVAILABLE /
 * NOT_WORKING. The `unique (user_id, date)` key from `0007` is what makes
 * re-marking a day an upsert rather than an accumulation of contradictory
 * rows — a day is one thing.
 *
 * `note` is free text for the day sheet; `undefined` and `null` both write a
 * database `null` (no note), they never leave a stale note behind on a row
 * being re-marked.
 */
export async function upsertException(
  userId: string,
  input: { date: IsoDate; kind: DayException['kind']; note?: string | null },
): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase
    .from('day_exceptions')
    .upsert(
      { user_id: userId, date: input.date, kind: input.kind, note: input.note ?? null },
      { onConflict: 'user_id,date' },
    )
  return { error }
}

/** Clears one of the user's exceptions. Scoped by `user_id` as well as `id`. */
export async function deleteException(
  userId: string,
  exceptionId: string,
): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase
    .from('day_exceptions')
    .delete()
    .eq('id', exceptionId)
    .eq('user_id', userId)
  return { error }
}

// ---------------------------------------------------------------------------
// shifts — the FWA manual path
// ---------------------------------------------------------------------------

/**
 * Adds one hand-entered FWA shift (`origin: 'FWA_MANUAL'`). `status` is not
 * sent — the column default (`'ROSTERED'`) is the database's to apply, and a
 * client-sent status would be a second source of truth for a value nothing
 * may set this phase (no UPDATE grant exists on `shifts`).
 *
 * The `unique (owner_user_id, work_date, shift_code)` key makes a duplicate a
 * 23505 unique violation, returned here as `{ shift: null, duplicate: true,
 * error: null }` — a typed, expected outcome the sheet renders as "you
 * already have that shift on that day", never a bare failure.
 */
export async function addFwaShift(
  userId: string,
  input: { date: IsoDate; code: ShiftCode },
): Promise<AddFwaShiftResult> {
  const { data, error } = await supabase
    .from('shifts')
    .insert({
      owner_user_id: userId,
      work_date: input.date,
      shift_code: input.code,
      origin: 'FWA_MANUAL',
    })
    .select()
    .single()

  if (error) {
    if (error.code === '23505') return { shift: null, duplicate: true, error: null }
    return { shift: null, duplicate: false, error }
  }
  return { shift: toExplicitShift(data as ShiftRow), duplicate: false, error: null }
}

/**
 * Removes one FWA shift — a hard DELETE, the plan's ruling for this phase:
 * no Phase 3 row has identity beyond its owner, so there is nothing to
 * retire gracefully yet. Scoped by `owner_user_id` as well as `id`.
 */
export async function deleteFwaShift(
  userId: string,
  shiftId: string,
): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase
    .from('shifts')
    .delete()
    .eq('id', shiftId)
    .eq('owner_user_id', userId)
  return { error }
}

// ---------------------------------------------------------------------------
// recurrences — with the regeneration-safety guard
// ---------------------------------------------------------------------------

/**
 * Counts the rule's generated `shifts` rows whose `status` is anything but
 * `ROSTERED`. A `head: true, count: 'exact'` request fetches the count and
 * no rows. These are the rows an edit or delete would destroy with identity
 * still attached — in Phase 3 none can exist (nothing writes a status yet),
 * but the guard is written now so Phase 4's advertised rows cannot be
 * silently eaten by an edit made in another tab.
 */
async function countNonRosteredRows(
  ruleId: string,
): Promise<{ count: number; error: PostgrestError | null }> {
  const { count, error } = await supabase
    .from('shifts')
    .select('id', { count: 'exact', head: true })
    .eq('recurrence_id', ruleId)
    .neq('status', 'ROSTERED')
  return { count: count ?? 0, error }
}

/**
 * Generates the rule's rows from its own start date out to
 * `min(end_date ?? horizon, horizon)` and inserts them
 * `on conflict do nothing` — the same idempotence and collision semantics as
 * lazy extension. Returns how many generated dates were skipped because a
 * row already occupied `(owner, work_date, shift_code)` — a hand-added
 * manual shift wins (the plan's collision ruling) — computed by comparing
 * the dates generated against the rows the database actually accepted
 * (`returning` only reports inserts, so the difference is the skip count the
 * recurrence sheet displays).
 */
async function generateRecurrenceRows(
  userId: string,
  ruleId: string,
  rule: RecurrenceRule,
  today: IsoDate,
): Promise<{ skippedCount: number | null; error: PostgrestError | null }> {
  const horizon = horizonForward(today)
  const bound = rule.endDate != null && rule.endDate <= horizon ? rule.endDate : horizon
  const rows = expandRecurrenceToRows(rule, { from: rule.startDate, to: bound }).map((row) => ({
    owner_user_id: userId,
    work_date: row.date,
    shift_code: row.code,
    origin: row.origin,
    recurrence_id: ruleId,
  }))
  if (rows.length === 0) return { skippedCount: 0, error: null }

  const { data, error } = await supabase
    .from('shifts')
    .upsert(rows, { onConflict: 'owner_user_id,work_date,shift_code', ignoreDuplicates: true })
    .select('id')
  if (error) return { skippedCount: null, error }
  return { skippedCount: rows.length - (data?.length ?? 0), error: null }
}

/**
 * Creates a rule (`rule.id` absent) or saves an edit to one (`rule.id`
 * present), then generates its rows. One function for both paths because
 * they share everything that matters — the guard, the regeneration, the
 * collision counting.
 *
 * Order matters, three times:
 *
 * 1. `validateRecurrenceRule` runs before **any** write. A malformed rule
 *    must throw with nothing deleted — the edit path deletes the rule's rows
 *    before reinserting, and validating any later would destroy a roster on
 *    a rule the engine was always going to refuse.
 * 2. The edit path's regeneration-safety guard runs before the deletes: if
 *    any of the rule's rows has a `status` other than `ROSTERED`, the call
 *    returns `{ guard: { kind: 'NON_ROSTERED_ROWS', count }, error: null }`
 *    and touches nothing (plan §1 Client, "Regeneration safety").
 * 3. On the edit path the rule itself is upserted **before** its rows are
 *    deleted. The rule write is the one step that can fail on the rule's own
 *    content (a CHECK rejection — an interval of 400 is one typo away) or on
 *    a transient error, and deleting first would leave the rule looking
 *    normal on the calendar with zero rows under it. This way a failed rule
 *    write leaves the old rows exactly as they were.
 *
 * Then: delete the rule's generated rows, and regenerate out to
 * `min(end_date, horizon)` — where manual rows win any collision and the
 * number of skipped dates comes back as `skippedCount` for the sheet to
 * sentence ("2 dates already have a shift on them").
 *
 * `today` anchors the horizon; it defaults to `todayIso()` and is injected
 * in tests. A malformed rule throws (`TypeError`/`RangeError` from the
 * validator) — callers composing one from UI state validate first, exactly
 * as `validateRecurrenceRule`'s export exists for.
 */
export async function saveRecurrence(
  userId: string,
  rule: RecurrenceRule,
  today: IsoDate = todayIso(),
): Promise<SaveRecurrenceResult> {
  // Before any write: a rule the engine would refuse must not get as far as
  // deleting its own rows.
  validateRecurrenceRule(rule)

  let saved: RecurrenceRule

  if (rule.id != null) {
    const { count, error: guardError } = await countNonRosteredRows(rule.id)
    if (guardError) return { rule: null, skippedCount: null, guard: null, error: guardError }
    if (count > 0) {
      return { rule: null, skippedCount: null, guard: { kind: 'NON_ROSTERED_ROWS', count }, error: null }
    }

    // The rule write first: if it fails — on a CHECK the validator mirrors,
    // or on a transient error — the rule's generated rows below are still
    // exactly where they were. Deleting first would leave the rule listed
    // and looking normal with zero rows on the calendar.
    const { data, error } = await supabase
      .from('recurrences')
      .upsert({ id: rule.id, ...ruleToRow(rule, userId) }, { onConflict: 'id' })
      .select()
      .single()
    if (error) return { rule: null, skippedCount: null, guard: null, error }
    saved = toRecurrenceRule(data as RecurrenceRow)

    const { error: deleteError } = await supabase
      .from('shifts')
      .delete()
      .eq('recurrence_id', rule.id)
      .eq('owner_user_id', userId)
    if (deleteError) return { rule: null, skippedCount: null, guard: null, error: deleteError }
  } else {
    const { data, error } = await supabase
      .from('recurrences')
      .insert(ruleToRow(rule, userId))
      .select()
      .single()
    if (error) return { rule: null, skippedCount: null, guard: null, error }
    saved = toRecurrenceRule(data as RecurrenceRow)
  }

  const { skippedCount, error: generateError } = await generateRecurrenceRows(
    userId,
    saved.id as string,
    saved,
    today,
  )
  if (generateError) return { rule: null, skippedCount: null, guard: null, error: generateError }
  return { rule: saved, skippedCount: skippedCount as number, guard: null, error: null }
}

/**
 * Deletes a rule; the `on delete cascade` from `recurrences` to `shifts`
 * (`0009`) removes its generated rows, so there is no client-side delete to
 * keep in step. But the cascade is exactly what makes the guard
 * non-negotiable — it would take a Phase 4 advertised row with it — so the
 * same `countNonRosteredRows` check runs first, and a non-zero count returns
 * the typed guard error with the rule untouched.
 */
export async function deleteRecurrence(
  userId: string,
  ruleId: string,
): Promise<DeleteRecurrenceResult> {
  const { count, error } = await countNonRosteredRows(ruleId)
  if (error) return { guard: null, error }
  if (count > 0) return { guard: { kind: 'NON_ROSTERED_ROWS', count }, error: null }

  const { error: deleteError } = await supabase
    .from('recurrences')
    .delete()
    .eq('id', ruleId)
    .eq('owner_user_id', userId)
  return { guard: null, error: deleteError }
}
